import {
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import Button from "../buttons/Button";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CaseMockResponse } from "../../onboarding/mock";
import React from "react";
import onBoardingSteps from "../../lang/en/onboarding.json";
import {scrollToElement} from "../../utils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  backgroundColor: "#B388FF",
  color: "white",
  boxShadow: 24,
  p: 4,
  zIndex: 10000,
  borderRadius: 4,
};

const DimmedOverlay: React.FC<{
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}> = ({ x, y, width, height }) => {
  const [viewportDimensions, setViewportDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setViewportDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 50,
      }}
    >
      <svg
        style={{ width: "100%", height: "100%" }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <defs>
          <mask id="spotlight">
            <rect width="100" height="100" fill="white" />
            {x !== undefined && y !== undefined && width !== undefined && height !== undefined && (
              <rect
                x={(x / viewportDimensions.width) * 100}
                y={(y / viewportDimensions.height) * 100}
                width={(width / viewportDimensions.width) * 100}
                height={(height / viewportDimensions.height) * 100}
                rx={1.4}
                ry={1.4}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100"
          height="100"
          fill="rgba(0, 0, 0, 0.4)"
          mask="url(#spotlight)"
        />
      </svg>
    </Box>
  );
};

const OnboardingModal: React.FC<{
  //   title: string;
  //   description: string;
  //   open: boolean;
  //   onClose: () => void;
  //   prevLabel?: string;
  //   skipLabel?: string;
}> = (
  {
    //   title,
    //   description,
    //   open,
    //   onClose,
    //   prevLabel = "Previous",
    //   skipLabel = "Skip Guide",
  }
) => {
  const [targetRect, setTargetRect] = React.useState<DOMRect | null>(null);
  const [onboardingStep, setOnboardingStep] = React.useState<number | null>(
    null
  );

  const { id: caseId, revisionId } = useParams();
  const navigate = useNavigate();

  const open = useMemo(() => onboardingStep !== null, [onboardingStep]);

  useEffect(() => {
    if (onboardingStep !== null) {
      const itemId = onBoardingSteps[onboardingStep].item;
      if (!itemId) {
        // Clear the targetRect when item is null
        setTargetRect(null);
        return;
      }

      const elm = document.getElementById(itemId);
      if (elm) {
        scrollToElement(elm);
        const timer = setTimeout(() => {
          setTargetRect(elm.getBoundingClientRect());
        }, 200);
        return () => clearTimeout(timer);
      }
    }
  }, [onboardingStep]);

  useEffect(() => {
    if (onboardingStep === null && caseId === CaseMockResponse.id) {
      setOnboardingStep(0);
    }
  }, [onboardingStep]);

  useEffect(() => {
    if (onboardingStep === null) {
      return;
    }

    const handleScroll = () => {
      const itemId = onBoardingSteps[onboardingStep].item;
      if (!itemId) {
        // Keep targetRect null when item is null
        setTargetRect(null);
        return;
      }

      const elm = document.getElementById(itemId);
      if (elm) {
        setTargetRect(elm.getBoundingClientRect());
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [onboardingStep]);


  const modalPosition = useMemo(() => {
    const modalWidth = 400;
    const bigMargin = 50;
    const smallMargin = 10;

    if (!targetRect) {
      return { left: "50%", top: "50%" };
    }

    let top = targetRect.top - targetRect.height / 2;
    let left = targetRect.right;

    if (targetRect.width > 0.7 * window.innerWidth) { //target takes the width of window
      left = window.innerWidth / 2; // center x
      if (targetRect.height < 0.6* window.innerHeight && window.innerHeight  - targetRect.bottom > 0.3* window.innerHeight) { // target height shorter than screen and there is room below
        if (targetRect.height > 0.4*window.innerHeight) {
          top = targetRect.bottom + bigMargin; // place below
        } else {
          top = targetRect.bottom + smallMargin + document.getElementById('onboarding-modal')!.getBoundingClientRect().height/2;
        }
      } else if (targetRect.height < 0.6* window.innerHeight && window.innerHeight  - targetRect.bottom <= 0.3* window.innerHeight) { // target height shorter than screen and there is room above
        if (targetRect.height > 0.4*window.innerHeight) {
          top = targetRect.top - bigMargin; //place above
        } else  {
          top = targetRect.top - smallMargin - + document.getElementById('onboarding-modal')!.getBoundingClientRect().height/2;
        }
      } else {
        // top = targetRect.top - targetRect.height;
        top = window.innerHeight/2;
      }
    }
    return { top, left };
  }, [targetRect]);

  const handleOnboarding = (direction: "next" | "prev") => {
    if (direction === "next" && onboardingStep === onBoardingSteps.length - 1) {
      window.location.href = "/cases";
      return;
    }

    const factor = direction === "next" ? 1 : -1;
    const nextStepIndex = (onboardingStep as number) + factor;
    const path = onBoardingSteps[nextStepIndex].url;

    // Dispatch event before navigation
    const event = new CustomEvent('onboardingStepChange', {
      detail: {
        showMockFiles: onBoardingSteps[nextStepIndex].showMockFiles
      }
    });
    window.dispatchEvent(event);

    const curPath = window.location.pathname.split("/").slice(-2).join("/");
    if (curPath !== path) {
      navigate(`/cases/${caseId}/${path}`, { replace: true });
    }
    if (onboardingStep === 0 && direction === "prev") {
      return;
    }
    setTimeout(() => {
      setOnboardingStep(nextStepIndex);
    }, 200);

  };

  useEffect(() => {
    if (onboardingStep !== null) {
      const event = new CustomEvent('onboardingStepChange', {
        detail: {
          showMockFiles: onBoardingSteps[onboardingStep].showMockFiles
        }
      });
      window.dispatchEvent(event);
    }
  }, [onboardingStep]);

  const nextLabel = useMemo(
    () => (onboardingStep === 0 ? "Next" : "Next"),
    [onboardingStep]
  );

  return (
    <>
      <Stack
          id={"onboarding-modal"}
        maxWidth={470}
        width={470}
        spacing={2}
        sx={{ ...style, ...modalPosition, display: open ? "block" : "none" }}
      >
        <Typography textAlign={"center"} variant="body1" fontWeight={"bold"}>
          {onBoardingSteps[Number(onboardingStep)].title}
        </Typography>
        <Typography textAlign={"center"} variant="caption">
          {onBoardingSteps[Number(onboardingStep)].description}
        </Typography>

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          spacing={2}
        >
          {Number(onboardingStep) > 0 && (
            <Button
              onClick={() => handleOnboarding("prev")}
              variant={"text"}
              width={"auto"}
              sx={{ color: "white" }}
            >
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <KeyboardArrowLeft fontSize="small" />
                <Typography variant="caption">{"Previous"}</Typography>
              </Stack>
            </Button>
          )}
          <Button
            onClick={() => handleOnboarding("next")}
            variant={"purple"}
            width={"auto"}
          >
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <KeyboardArrowRight fontSize="small" />
              <Typography variant="caption">{nextLabel}</Typography>
            </Stack>
          </Button>
          <Button
            onClick={() => {
              setOnboardingStep(null);
              window.location.href = "/cases";
            }}
            variant={"text"}
            width={"auto"}
            sx={{ color: "white" }}
          >
            <Stack direction={"row"} spacing={1} alignItems={"center"}>
              <Close htmlColor="#5E35B1" fontSize="small" />
              <Typography
                whiteSpace={"nowrap"}
                color="#5E35B1"
                variant="caption"
              >
                {"Skip Guide"}
              </Typography>
            </Stack>
          </Button>
        </Stack>
      </Stack>
      {onboardingStep !== null && (
        <DimmedOverlay
          {...(targetRect ? {
            x: targetRect.x,
            y: targetRect.y,
            width: targetRect.width,
            height: targetRect.height,
          } : {})}
        />
      )}
    </>
  );
};

export default OnboardingModal;
